import { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { LogOut, Settings, UserRound } from "lucide-react";
import { NotificationBell } from "@/components/notification-bell";
import { NoiumIcon } from "@/components/noium-icon";
import { ProfileDialog } from "@/components/profile-dialog";


type NavItemKey =
  | "opportunities"
  | "clients"
  | "contacts"
  | "lemlist"
  | "settings";

type NavItem = {
  to: string;
  label: string;
  end?: boolean;
  key: NavItemKey;
  adminOnly?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Opportunities", end: true, key: "opportunities" },
  { to: "/clients", label: "Clients", key: "clients" },
  { to: "/contacts", label: "Contacts", key: "contacts" },
  { to: "/lemlist", label: "Lemlist", key: "lemlist" },
  { to: "/settings", label: "Settings", key: "settings", adminOnly: true },
];

export function AppShell() {
  const navigate = useNavigate();
  const me = trpc.auth.me.useQuery();
  const user = me.data;
  const [profileOpen, setProfileOpen] = useState(false);
  const enabled = !!user;
  const isAdmin = user?.role === "admin";
  const opportunitiesQuery = trpc.opportunities.list.useQuery({}, { enabled });
  const clientsQuery = trpc.clients.list.useQuery({}, { enabled });
  const contactsQuery = trpc.contacts.list.useQuery(undefined, { enabled });

  const counts: Record<NavItemKey, number | undefined> = {
    opportunities: opportunitiesQuery.data?.length,
    clients: clientsQuery.data?.length,
    contacts: contactsQuery.data?.length,
    lemlist: undefined,
    settings: undefined,
  };

  const visibleNavItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? "?";

  const showHeader = !!user;

  return (
    <div className="min-h-screen bg-background">
      {showHeader && (
      <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 w-full max-w-screen-2xl items-center justify-between gap-2 px-4 md:px-6">
          <div className="flex min-w-0 items-center gap-6">
            <Link to="/" className="flex shrink-0 items-center gap-2 font-semibold">
              <NoiumIcon size={28} />
              <span className="hidden sm:inline">Noium Quotes</span>
            </Link>
            <NavigationMenu className="hidden md:block">
              <NavigationMenuList className="gap-2">
                {visibleNavItems.map((item) => {
                  const count = counts[item.key];
                  return (
                  <NavigationMenuItem key={item.to}>
                    <NavigationMenuLink asChild>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            navigationMenuTriggerStyle(),
                            "h-8 gap-1.5 px-3 text-sm font-medium text-muted-foreground hover:text-foreground",
                            isActive && "bg-accent text-accent-foreground"
                          )
                        }
                      >
                        {item.label}
                        {count ? (
                          <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium tabular-nums text-muted-foreground">
                            {count}
                          </span>
                        ) : null}
                      </NavLink>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <NotificationBell />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <Avatar>
                      {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.email} />}
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium">{user.name ?? user.email}</span>
                    {user.name && (
                      <span className="text-xs font-normal text-muted-foreground">
                        {user.email}
                      </span>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setProfileOpen(true)}>
                    <UserRound className="mr-2 h-4 w-4" />
                    My profile
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onSelect={() => navigate("/settings")}>
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <a href="/auth/logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <a
                href="/auth/login"
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Sign in
              </a>
            )}
          </div>
        </div>

        {/* Mobile nav: horizontally scrollable pills, always visible */}
        <nav
          aria-label="Main navigation"
          className="border-t md:hidden"
        >
          <div className="-mx-px overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max items-center gap-1 px-3 py-2">
              {visibleNavItems.map((item) => {
                const count = counts[item.key];
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                        isActive
                          ? "border-transparent bg-accent text-accent-foreground"
                          : "border-border bg-background text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                      )
                    }
                  >
                    <span>{item.label}</span>
                    {count ? (
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium tabular-nums text-muted-foreground">
                        {count}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          </div>
        </nav>
      </header>
      )}

      <main className="w-full">
        {me.isLoading ? (
          <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : !user ? (
          <LoginScreen />
        ) : (
          <Outlet />
        )}
      </main>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}

function LoginScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm rounded-xl border bg-card p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <NoiumIcon size={48} />
          <h1 className="text-xl font-semibold">Noium Quotes</h1>
          <p className="text-sm text-muted-foreground">
            Sign in with your Noium Google account to continue.
          </p>
        </div>
        <a
          href="/auth/login"
          className="flex h-11 w-full items-center justify-center gap-3 rounded-md border bg-background text-sm font-medium text-foreground transition hover:bg-accent"
        >
          <GoogleMark />
          Sign in with Google
        </a>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Access is limited to users listed in Members.
        </p>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.806.54-1.8368.8595-3.0477.8595-2.344 0-4.3282-1.5832-5.0359-3.7104H.9573v2.3318C2.4382 15.9831 5.4818 18 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.9641 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.9641 10.71z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9641 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
        fill="#EA4335"
      />
    </svg>
  );
}

